import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useMe } from "@/auth/useMe";
import { Card, EmptyState, Page } from "@/components/ui";
import { NuevoContacto } from "@/empresas/NuevoContacto";
import { useSettled } from "@/lib/useSettled";
import { AssignBar } from "@/recepcion/AssignBar";
import { CallForm } from "@/recepcion/CallForm";
import { CallRegistered } from "@/recepcion/CallRegistered";
import type { CallDraft } from "@/recepcion/model";
import {
  EMPTY_DRAFT,
  blockerFor,
  callBody,
  isWorthSearching,
  searchQuery,
} from "@/recepcion/model";
import type { InboundCallResult, ReceptionCompany } from "@/recepcion/queries";
import {
  useCompanyFile,
  useRegisterCall,
  useSalesAgents,
  useSimilarities,
} from "@/recepcion/queries";
import type { Capture } from "@/recepcion/rows";
import { Similar } from "@/recepcion/Similar";

/** Long enough that a name being typed is one search, not one per letter. */
const SEARCH_DELAY = 300;
/** `Contact.DataSource.INBOUND_CALL`. */
const INBOUND_CALL = "3";

/** The heading of one step: its number in the system's red, then what to do. */
function Step({ number, children }: { number: number; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <h2 className="mb-2 text-[20px]">
      <span className="text-brand">{t("reception.step", { number })}</span> {children}
    </h2>
  );
}

/**
 * Recepción — the capture window for an inbound call.
 *
 * One screen, three numbered steps, in the order they are done: what the
 * caller said (five fields), who takes the call (*Asignar a*, which pins to
 * the top once scrolled past), and who in the book that might be (four
 * sections that search as the fields fill in). Nothing is saved until a button
 * hands the call to a seller, and then all of it is — the lead, the timeline
 * entry, and the company or the assignment if either was missing — in one
 * request (`crm.services.reception`).
 */
export function NuevaLlamada() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  // The same check `IsTriageStaff` makes. Unlike the queue, this screen is not
  // even readable without it: what it searches is the whole book.
  const canTriage = me?.can_triage === true || me?.is_manager === true;

  const [draft, setDraft] = useState<CallDraft>(EMPTY_DRAFT);
  const [agentId, setAgentId] = useState("");
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const [contactFor, setContactFor] = useState<ReceptionCompany | null>(null);
  const [registered, setRegistered] = useState<InboundCallResult | null>(null);

  const agents = useSalesAgents(canTriage);
  const register = useRegisterCall();
  const agent = agents.data?.find((option) => String(option.id) === agentId) ?? null;

  // Settled field by field: each is a string, so a keystroke in one does not
  // restart the wait on the other three.
  const typed = searchQuery(draft);
  const companyName = useSettled(typed.company_name, SEARCH_DELAY);
  const contactName = useSettled(typed.contact_name, SEARCH_DELAY);
  const phone = useSettled(typed.phone, SEARCH_DELAY);
  const email = useSettled(typed.email, SEARCH_DELAY);
  const query = useMemo(
    () => ({ company_name: companyName, contact_name: contactName, phone, email }),
    [companyName, contactName, phone, email],
  );
  // The page of *Palabras clave*, kept with the name it was turned on: another
  // name is another list, and it starts on its first page.
  const [keywordsPaged, setKeywordsPaged] = useState({ name: "", page: 1 });
  const keywordsPage = keywordsPaged.name === companyName ? keywordsPaged.page : 1;
  const similar = useSimilarities(
    query,
    keywordsPage,
    canTriage && isWorthSearching(query),
  );

  // The company a contact is being added to: its branches are what the modal
  // needs before it can open, and the row may never have been expanded.
  const file = useCompanyFile(contactFor?.id ?? 0, contactFor !== null);

  function hand(decided: { company?: number; contact?: number }) {
    register.mutate(callBody(draft, { agent: agent?.id ?? null, ...decided }), {
      onSuccess: setRegistered,
    });
  }

  const capture: Capture = {
    draft,
    agent,
    busy: register.isPending,
    assign: (company, contact) => hand({ company: company.id, contact: contact.id }),
    addContact: setContactFor,
    expanded,
    toggle: (companyId) =>
      setExpanded((previous) => {
        const next = new Set(previous);
        if (next.has(companyId)) next.delete(companyId);
        else next.add(companyId);
        return next;
      }),
  };

  function startOver() {
    setDraft(EMPTY_DRAFT);
    setAgentId("");
    setExpanded(new Set());
    setKeywordsPaged({ name: "", page: 1 });
    setRegistered(null);
    register.reset();
  }

  function contactCreated(company: ReceptionCompany) {
    // Open the company's list wherever it is on screen: the person just added
    // is in it now, with the button that hands them the call.
    setExpanded((previous) => new Set(previous).add(company.id));
    void queryClient.invalidateQueries({ queryKey: ["reception"] });
  }

  const error =
    (register.error instanceof Error ? register.error.message : null) ??
    (file.isError ? t("errors.receptionCompany") : null);

  return (
    <Page
      title={t("reception.title")}
      subtitle={t("leads.title")}
      actions={
        <Link to="/recepcion" className="btn btn-ghost">
          {t("reception.backToList")}
        </Link>
      }
    >
      {!canTriage ? (
        <Card>
          <EmptyState>{t("reception.noAccess")}</EmptyState>
        </Card>
      ) : registered ? (
        <CallRegistered
          result={registered}
          canOpenCompany={me?.is_manager === true}
          onAnother={startOver}
        />
      ) : (
        <>
          <Step number={1}>{t("reception.steps.call")}</Step>
          <div className="mb-6">
            <Card>
              <CallForm draft={draft} onChange={setDraft} />
            </Card>
          </div>

          <Step number={2}>{t("reception.steps.agent")}</Step>
          <AssignBar
            agents={agents.data ?? []}
            value={agentId}
            onChange={setAgentId}
            blocker={blockerFor(draft, agent?.id ?? null, null)}
            error={error}
          />

          <div className="flex flex-wrap items-baseline gap-x-3">
            <Step number={3}>{t("reception.steps.similar")}</Step>
            <p role="status" className="mb-2 text-xs text-mute-600">
              {similar.isFetching ? t("reception.searching") : null}
            </p>
          </div>

          <Similar
            found={isWorthSearching(query) ? similar.data : undefined}
            query={query}
            capture={capture}
            onKeywordsPage={(page) => setKeywordsPaged({ name: companyName, page })}
            paging={similar.isFetching}
            onCreateCompany={() => hand({})}
          />

          {contactFor && file.data ? (
            <NuevoContacto
              open
              onClose={() => setContactFor(null)}
              companyId={contactFor.id}
              companyName={contactFor.name || contactFor.legal_name}
              branches={file.data.branches}
              initialName={draft.contactName.trim()}
              initialPhone={draft.phone.trim()}
              initialEmail={draft.email.trim()}
              initialDataSource={INBOUND_CALL}
              onCreated={() => contactCreated(contactFor)}
            />
          ) : null}
        </>
      )}
    </Page>
  );
}
